import { Injectable } from "@angular/core";

declare var window;

@Injectable({
	providedIn: "root",
})
export class GoogleAdManagerService {
	eventsAdded = false;
	initialResize = window.innerWidth;
	private initPromise: Promise<void> = Promise.resolve();

	constructor() {
		window['loadAdXAds'] = this.loadAdXAds.bind(this);
		window['validateEnv'] = this.validateEnv.bind(this);
		window['browserResize'] = this.browserResize.bind(this);
	}

	initGoogleAdManager() {
		const that = this;
		window.googletag = window.googletag || { cmd: [] };

		// Hash PPID before cmd.push to guarantee enableServices() runs before display().
		const raw = this.getPreferredRawIdForPpid();
		const ppidPromise = (raw && this.getConsentCookieValue('C0004') && this.getConsentCookieValue('C0002'))
			? this.sha256HexAsync(raw.trim())
			: Promise.resolve(null);

		this.initPromise = ppidPromise.then(function(ppid) {
			window.googletag.cmd.push(function() {
				window.googletag.pubads().setPrivacySettings({
					restrictDataProcessing: true,
					limitedAds: !that.getConsentCookieValue('C0004')
				});
				if (ppid) window.googletag.pubads().setPublisherProvidedId(ppid);
				window.googletag.enableServices();
			});
		});

		if (window.addEventListener) {
			window.addEventListener("resize", window.browserResize);
		} else if (window.attachEvent) {
			window.attachEvent("onresize", window.browserResize);
		}
	}

	// SHA-256 hash via Web Crypto API (async, returns null if unavailable or on error)
	sha256HexAsync(str: string): Promise<string | null> {
		if (!window.crypto?.subtle) return Promise.resolve(null);
		const bytes = new TextEncoder().encode(str);
		return crypto.subtle.digest('SHA-256', bytes)
			.then(buf => Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join(''))
			.catch(() => null);
	}

	// Returns the best available raw ID for PPID: club card first, AMCV MCMID as fallback
	getPreferredRawIdForPpid(): string | null {
		const clubCard = window['AB']?.userInfo?.clubCard;
		if (clubCard) return String(clubCard);
		const amcvCookie = window['AB']?.COMMON?.getCookie('AMCV_A7BF3BC75245ADF20A490D4D@AdobeOrg');
		if (amcvCookie) {
			const amcv = amcvCookie.split('|');
			const mcmidIndex = amcv.indexOf('MCMID');
			if (mcmidIndex !== -1 && amcv[mcmidIndex + 1]) return String(amcv[mcmidIndex + 1]);
		}
		return null;
	}

	browserResize() {
		let afterResize = window.innerWidth;
		if (
			(this.initialResize < 1440 && afterResize >= 1440) ||
			(this.initialResize >= 1440 && afterResize < 1440) ||
			(this.initialResize < 1024 && afterResize >= 1024) ||
			(this.initialResize >= 1024 && afterResize < 1024) ||
			(this.initialResize < 768 && afterResize >= 768) ||
			(this.initialResize >= 768 && afterResize < 768) ||
			(this.initialResize < 360 && afterResize >= 360) ||
			(this.initialResize >= 360 && afterResize < 360)
		) {
			this.initialResize = afterResize;
			const filteredSlots = window.googletag.pubads()
				.getSlots()
				.filter((item) => item.getSizes().length > 1);

			for (let i = 0; i < filteredSlots.length; i++) {
				window.googletag.cmd.push(function () {
					window.googletag.pubads().refresh([filteredSlots[i]], {
						changeCorrelator: false,
					});
				});
			}
		}
	}

	validateEnv(adUnitPath, sizes) {
		const regex = /(nonprod)/;
		const str = adUnitPath.match(regex);
		if (str && str.length > 0 && sizes.length > 1 || str && str.length > 0 && sizes[0] != "fluid") {
			return true;
		} else {
			return false;
		}
	}

	// Function to add content to page
	slotGenerator(data: any, elem: string) {
		const that = this;
		const run = function() {
			window.googletag.cmd.push(function () {
				var slot = window.validateEnv(data.adUnitPath, data.sizes) ?
					window.googletag.defineSlot(data.adUnitPath, data.sizes, elem)
						.set("adsense_test_mode", "on")
						.addService(window.googletag.pubads()) :
					window.googletag.defineSlot(data.adUnitPath, data.sizes, elem)
						.addService(window.googletag.pubads());

				if (data.sizeMapping) {
					slot.defineSizeMapping(data.sizeMapping);
				}

				if (data.targeting) {
					data.targeting.forEach((obj) => {
						const key = Object.keys(obj)[0]; //gets key value
						slot.setTargeting(key, obj[key]);
					});
				}
				window.googletag.display(slot);
				if (!that.eventsAdded) {
					window.googletag.pubads().addEventListener("slotResponseReceived", function (e) {
						if (e.slot.getAdUnitPath().includes('bottomb1') && e.slot.getResponseInformation()) {
							// Add horizontal bar as non endemic AD is loaded
							document.getElementById('rewardsAdBottom').className += 'googleAd-border d-flex justify-content-center';
						}
					});

					window.googletag.pubads().addEventListener("slotOnload", function(e) {
						const getDivId = document.getElementById("google_ads_iframe_" + e.slot.getSlotId().getId());
						getDivId.setAttribute('role', 'none');
					});

					that.eventsAdded = true;
				}
			});
		};
		this.initPromise.then(run);
	}

	loadAdXAds(adConfig) {
		this.slotGenerator(adConfig, "rewardsAdBottom");
	}

	gamScriptLoaded(isloaded, banner) {
		window.checkAdBlocker = true;
		if (!isloaded) { //adblocker is enabled
			window.googletag._loaded_ = false;
			this.deleteGAMCookies(banner);
		}else{
			this.checkCookieConsentAndSetGAMoptions(banner);
		}
		window.dispatchEvent(new CustomEvent('adBlockerLoadChecked', { detail: { adblockerEnabled: isloaded } }));
	}

	checkCookieConsentAndSetGAMoptions(banner) {
		const oneTrustLoadedEvent = window['dataLayer'] && window['dataLayer'].find(x => x.event === 'OneTrustGroupsUpdated');
		if (oneTrustLoadedEvent) {
			if (oneTrustLoadedEvent?.['OnetrustActiveGroups'].includes('C0004')) {
				this.setLimitedAdsOption(false);
			}
			else {
				this.setLimitedAdsOption(true);
				this.deleteGAMCookies(banner);
			}
		}
		else {
			// OneTrustLoaded event not fired yet - listen & then check if C0004 part
			window.addEventListener("OneTrustGroupsUpdated", (eventResponse) => {
				const oneTrustLoadedEventResponse = eventResponse && eventResponse['detail'];
				if (oneTrustLoadedEventResponse && oneTrustLoadedEventResponse.includes('C0004')) {
					this.setLimitedAdsOption(false);
				} else {
					this.setLimitedAdsOption(true);
					this.deleteGAMCookies(banner);
				}
			}, { once: true });
		}
	}

	//update limitedAds option value
    public setLimitedAdsOption (value) {
        window['googletag'].cmd.push(function () {
            window['googletag'].pubads().setPrivacySettings({
                limitedAds: value
            });
        });
    }

    deleteGAMCookies = function (banner) {
        const GAMcookies = ["__gads", "__gpi", "__gpi_optout", "GED_PLAYLIST_ACTIVITY"];
        // "NID", "AEC", "1P_JAR", "IDE", "APC", "test_cookie", "DSID", "id" - these are 3rd party GAM related cookies, set on different domain (google, googlesyndication, doubleclick.net) and will not be populated once limitedAds is set to true.
        for (let cookieName of GAMcookies) {
            document.cookie = cookieName + `=; expires=Thu, 01-Jan-1970 00:00:01 GMT; path=/; domain=.${banner}.com`;
        }
    }

	getConsentCookieValue(cookieCode: string): boolean {
		const cookie = document.cookie.split('; ').find(row => row.startsWith('OptanonConsent='));
		if (!cookie) return true;
		return new RegExp(cookieCode + ':1').test(decodeURIComponent(cookie));
	}
}
